import { AbstractService } from 'src/services/AbstractService';
import { config } from 'src/lib/config';
import { ServiceError } from 'src/lib/errors';
import qs from 'qs';
import { CompositeMatchingParams, CompositeMatchingResponse } from 'src/types/compositeMatching';

export type MatcherType = 'template' | 'logo';
export type DesignCreationType = 'Static' | 'Dynamic' | 'AutoLayout';

export const buildCompositeMatchingUrl = (params: CompositeMatchingParams): { url: string; pathWithQuery: string } => {
    const path = `api/v3/Product/${encodeURIComponent(params.productKey)}`
        + `/version/${params.productVersion}`
        + `/Culture/${encodeURIComponent(params.culture)}/compositeMatching`;

    const searchParams = qs.stringify({
        runAllMatchers: true,
        noCache: true,
        debug: true,
        previewWidth: 824,
        requestor: config.appName,
        documentUrl: params.documentUrl,
        designPersonalizationContextId: params.designPersonalizationContextId,
        designConceptId: params.designConceptId,
        brandId: params.brandId,
        sourceProductKey: params.sourceProductKey,
        sourceProductVersion: params.sourceProductVersion,
        sourceProductOptions: params.sourceProductOptions,
        productOptions: params.productOptions,
        scenePurpose: params.scenePurpose,
        agGeneratePriority: params.agGeneratePriority,
    });

    const pathWithQuery = `${path}?${searchParams}`;

    return {
        url: `${config.services.designConductorServiceApi.prefixUrl}${pathWithQuery}`,
        pathWithQuery,
    };
};

class DesignConductorServiceApi extends AbstractService implements Services.DesignConductor.Content {
    public async getContent(
        productKey: string,
        productVersion: string,
        culture: string,
        designConceptId: string,
        selectedOptions?: Record<string, string>,
        signal?: AbortSignal,
    ): Promise<Models.DesignConductor.DesignContentResponse | null> {
        const url = `api/${this.version}/Galleries/dynamic__${productKey}__${productVersion}/Culture/${culture}/Content`;

        const queryParams: Record<string, string | string[]> = {
            designConceptIds: designConceptId,
            includeCategoryAndKeywordData: 'true',
            requestor: config.appName,
            limit: '1',
        };

        if (selectedOptions) {
            const attributes: string[] = [];

            Object.entries(selectedOptions).forEach(([key, value]) => {
                attributes.push(`${key.toLowerCase()}_${value.toLowerCase()}`);
            });
            if (attributes.length > 0) {
                queryParams.attributes = attributes;
            }
        }

        const searchParams = qs.stringify(queryParams, { arrayFormat: 'repeat' });

        try {
            const response = await this.api
                .get(
                    url,
                    {
                        searchParams,
                        signal,
                    },
                )
                .json<Models.DesignConductor.DesignContentResponse>();

            return response;
        } catch (e: any) {
            if (e.response?.status === 404) {
                return null;
            }

            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to fetch design content',
                info: (e as Error).message,
                url,
                productKey,
                productVersion,
                culture,
                designConceptId,
            });
        }
    }

    public async getCompositeMatching(
        params: CompositeMatchingParams,
        accessToken: string,
    ): Promise<CompositeMatchingResponse> {
        const { pathWithQuery } = buildCompositeMatchingUrl(params);
        const [url, searchParams] = pathWithQuery.split('?');

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                })
                .json<CompositeMatchingResponse>();
        } catch (e: any) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Composite matching failed',
                info: (e as Error).message,
                url,
                ...params,
            });
        }
    }
}

export const DesignConductorService = new DesignConductorServiceApi(config.services.designConductorServiceApi);
